import React, { Fragment, type ReactNode, useEffect, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { useAnalytics } from '../analytics/use-analytics';
import { useEditionAwareness } from '../services/edition-awareness-context';

import { ActionsAndContent } from './actions-and-content';

/**
 * One off event which tracks when a customer is eligible/ineligible for edition awareness
 */
function useTrackAudienceAndTiming(isEligible: boolean) {
	const { sendAnalyticsEvent } = useAnalytics();
	const hasSent = useRef(false);

	useEffect(() => {
		if (hasSent.current) {
			return;
		}

		sendAnalyticsEvent({
			eventType: 'operational',
			actionSubject: 'audienceAndTiming',
			actionSubjectId: 'editionAwareness',
			action: 'checked',
			attributes: {
				isEligible,
			},
		});

		hasSent.current = true;
	}, [sendAnalyticsEvent, isEligible]);
}

/**
 * Tracks when a user should see the edition awareness experience
 */
function Analytics({ children }: { children: ReactNode }) {
	useTrackAudienceAndTiming(true);

	return <Fragment>{children}</Fragment>;
}

/**
 * Just sends ineligibility info through a `eligibilityCriteria checked` event.
 * This exists so we can optionally render this component
 */
function IneligibleAnalytics() {
	useTrackAudienceAndTiming(false);

	return null;
}

type AudienceAndTimingProps = {
	trialButton?: ReactNode;
};

/**
 * Based on the audience and timing (e.g. 3 days before the trial ends) we decide to show
 * the free/trial button to the customer. If we want to change the logic of if we show the button or
 * not, it should happen here.
 *
 * Currently `trialButton` should assume they can render the button and it's only
 * the button content and action when the button is clicked that needs to be fetched/checked
 *
 * `trialButton` is planned for deprecation when the actions for dialogs is
 * migrated into this package.
 */
export function AudienceAndTiming({ trialButton }: AudienceAndTimingProps) {
	const context = useEditionAwareness();
	const {
		product,
		edition,
		isInTrial,
		isInPredunning,
		canUpgrade,
		isInvoiceable,
		canAddPaymentDetails,
	} = context;

	const isInTrialOrPredunning = isInTrial || isInPredunning;

	if (product === 'jira-product-discovery' && fg('jpd_disable_edition_awareness')) {
		return null;
	}

	if (edition === 'free' && canUpgrade) {
		return (
			<Analytics>
				<ActionsAndContent trialButton={trialButton} />
			</Analytics>
		);
	}

	if (
		edition === 'standard' &&
		isInTrialOrPredunning &&
		isInvoiceable === false &&
		canAddPaymentDetails
	) {
		return (
			<Analytics>
				<ActionsAndContent trialButton={trialButton} />
			</Analytics>
		);
	}

	if (
		edition === 'premium' &&
		['jira', 'jira-service-management'].includes(product) &&
		isInTrialOrPredunning &&
		isInvoiceable === false &&
		canAddPaymentDetails
	) {
		return (
			<Analytics>
				<ActionsAndContent trialButton={trialButton} />
			</Analytics>
		);
	}

	if (product === 'confluence' && edition === 'premium' && isInTrialOrPredunning) {
		// Confluence will always show the button for premium while a customer is in a trial or in predunning
		// If the customer can add payment, that is checked as part of rendering the dialog (as the dialog
		// will still show to the customer if they can not add payment details)
		return (
			<Analytics>
				<ActionsAndContent trialButton={trialButton} />
			</Analytics>
		);
	}

	if (edition === 'free' || isInTrialOrPredunning) {
		// We're only firing the ineligible analytics when we could've shown the button, not all instances
		// this is to ensure we don't send the event on every page load
		return <IneligibleAnalytics />;
	}

	return null;
}
